import { QueryClient } from '@tanstack/react-query'

import { BulkActionSession, BulkJobStatus, StoreWithDeployStatus } from '@/models/operations/types'

import { clearCache, executeWebhook } from './actions'
import { pollBulkRedeployDeployStatus } from './deployStatusPoll'
import {
  opsQueryKeys,
  setOptimisticActiveStoresBuilding,
  setOptimisticStoreBuilding,
} from './groupsCache'

/** Progress share while firing redeploy webhooks */
export const BULK_REDEPLOY_WEBHOOK_WEIGHT = 30
/** Progress share while waiting for deploy status to settle */
export const BULK_REDEPLOY_DEPLOY_STATUS_WEIGHT = 70

export function computeBulkJobProgress(job: BulkJobStatus | undefined): number {
  if (!job || job.total === 0) return 0
  if (job.status === 'completed' || job.phase === 'completed') return 100

  if (job.type === 'clear-cache') {
    return Math.round(((job.completed + job.failed) / job.total) * 100)
  }

  const webhookDone = job.completed + job.failed
  const webhookProgress = (webhookDone / job.total) * BULK_REDEPLOY_WEBHOOK_WEIGHT

  if (job.phase === 'webhooks') {
    return Math.round(Math.min(BULK_REDEPLOY_WEBHOOK_WEIGHT, webhookProgress))
  }

  const pollTotal = job.deployPollTotal ?? job.completed
  const pollResolved = job.deployPollResolved ?? 0
  const deployProgress =
    pollTotal > 0 ? (pollResolved / pollTotal) * BULK_REDEPLOY_DEPLOY_STATUS_WEIGHT : 0

  return Math.round(
    Math.min(100, BULK_REDEPLOY_WEBHOOK_WEIGHT + deployProgress),
  )
}

export function getBulkJobPhaseLabel(job: BulkJobStatus | undefined): string {
  if (!job) return 'Starting publish'

  if (job.type === 'clear-cache') {
    if (job.status === 'completed') return ''
    return job.completed + job.failed === 0
      ? 'Starting cache purge'
      : 'Purging cache for stores'
  }

  if (job.status === 'completed') return ''

  if (job.phase === 'webhooks') {
    return job.completed + job.failed === 0
      ? 'Starting publish'
      : 'Publishing stores'
  }
  if (job.phase === 'deploy-status') {
    const pollResolved = job.deployPollResolved ?? 0
    if (pollResolved === 0) {
      return 'Waiting before checking deployment status'
    }
    return 'Waiting for publish to complete'
  }

  return 'Starting publish'
}

function writeBulkActionSession(
  queryClient: QueryClient,
  session: BulkActionSession | null,
): void {
  queryClient.setQueryData(opsQueryKeys.bulkActionSession, session)
}

function writeBulkJob(queryClient: QueryClient, jobId: string, job: BulkJobStatus): void {
  queryClient.setQueryData(opsQueryKeys.bulkJob(jobId), job)
}

function patchBulkJob(
  queryClient: QueryClient,
  jobId: string,
  updater: (job: BulkJobStatus) => BulkJobStatus,
): void {
  const job = queryClient.getQueryData<BulkJobStatus>(opsQueryKeys.bulkJob(jobId))
  if (!job) return
  writeBulkJob(queryClient, jobId, updater(job))
}

let bulkActionLock = false

function createJobId() {
  return crypto.randomUUID()
}

export function getBulkActionSession(
  queryClient: QueryClient,
): BulkActionSession | null {
  return queryClient.getQueryData<BulkActionSession>(opsQueryKeys.bulkActionSession) ?? null
}

export function getBulkJob(queryClient: QueryClient, jobId: string): BulkJobStatus | undefined {
  return queryClient.getQueryData<BulkJobStatus>(opsQueryKeys.bulkJob(jobId))
}

export function isBulkRunningForGroup(queryClient: QueryClient, groupId: string): boolean {
  const session = getBulkActionSession(queryClient)
  return session?.groupId === groupId && session.status === 'in_progress'
}

export function isGroupDeployActionsLocked(
  queryClient: QueryClient,
  groupId: string,
  buildingCount: number,
): boolean {
  if (isBulkRunningForGroup(queryClient, groupId)) return true
  return buildingCount > 0
}

export function beginBulkActionSession(
  queryClient: QueryClient,
  groupId: string,
  type: BulkActionSession['type'],
  targetStoreIds?: string[],
): void {
  writeBulkActionSession(queryClient, {
    groupId,
    jobId: 'pending',
    type,
    status: 'in_progress',
    progressModalDismissed: false,
    targetStoreIds,
  })
}

export function getBulkRowActionStoreId(
  queryClient: QueryClient,
  groupId: string,
): string | null {
  const session = getBulkActionSession(queryClient)
  if (
    session?.groupId !== groupId ||
    session.status !== 'in_progress' ||
    session.type !== 'redeploy' ||
    session.targetStoreIds?.length !== 1
  ) {
    return null
  }
  return session.targetStoreIds[0]
}

export function dismissBulkProgressModal(queryClient: QueryClient): void {
  const session = getBulkActionSession(queryClient)
  if (!session) return
  writeBulkActionSession(queryClient, {
    ...session,
    progressModalDismissed: true,
  })
}

function setBulkActionSession(queryClient: QueryClient, session: BulkActionSession | null) {
  bulkActionLock = session?.status === 'in_progress'
  writeBulkActionSession(queryClient, session)
}

export function clearBulkActionSession(queryClient: QueryClient) {
  setBulkActionSession(queryClient, null)
}

export async function runBulkRedeploy(
  queryClient: QueryClient,
  stores: StoreWithDeployStatus[],
  groupId: string,
): Promise<string> {
  return runBulkJob(queryClient, 'redeploy', stores, groupId)
}

export async function runBulkClearCache(
  queryClient: QueryClient,
  stores: StoreWithDeployStatus[],
  groupId: string,
): Promise<string> {
  return runBulkJob(queryClient, 'clear-cache', stores, groupId)
}

async function runBulkJob(
  queryClient: QueryClient,
  type: BulkJobStatus['type'],
  stores: StoreWithDeployStatus[],
  groupId: string,
): Promise<string> {
  if (bulkActionLock && !getBulkActionSession(queryClient)) {
    bulkActionLock = false
  }

  const pendingSession = getBulkActionSession(queryClient)
  const isPendingForThisGroup =
    pendingSession?.groupId === groupId && pendingSession.jobId === 'pending'

  if (bulkActionLock && !isPendingForThisGroup) {
    throw new Error('A bulk action is already in progress')
  }

  const activeStores = stores.filter((s) => s.isActive)
  if (activeStores.length === 0) {
    throw new Error('No active stores to run this action')
  }

  const jobId = createJobId()
  const succeededStoreIds: string[] = []

  if (type === 'redeploy') {
    if (activeStores.length === 1) {
      setOptimisticStoreBuilding(queryClient, groupId, activeStores[0].id)
    } else {
      setOptimisticActiveStoresBuilding(queryClient, groupId)
    }
  }

  const sessionMeta = getBulkActionSession(queryClient)

  setBulkActionSession(queryClient, {
    groupId,
    jobId,
    type,
    status: 'in_progress',
    progressModalDismissed: sessionMeta?.progressModalDismissed ?? false,
    targetStoreIds: sessionMeta?.targetStoreIds,
  })

  writeBulkJob(queryClient, jobId, {
    jobId,
    status: 'in_progress',
    type,
    phase: 'webhooks',
    total: activeStores.length,
    completed: 0,
    failed: 0,
    errors: [],
  })

  const onWebhookProgress = ({ store, ok, error }: { store: StoreWithDeployStatus; ok: boolean; error?: string }) => {
    if (type === 'redeploy' && ok) {
      succeededStoreIds.push(store.id)
    }

    patchBulkJob(queryClient, jobId, (job) =>
      ok
        ? { ...job, completed: job.completed + 1 }
        : {
            ...job,
            failed: job.failed + 1,
            errors: [
              ...job.errors,
              {
                storeId: store.id,
                storeName: store.name,
                message: error ?? 'Unknown error',
              },
            ],
          },
    )
  }

  try {
    if (type === 'redeploy') {
      await executeWebhook(activeStores, undefined, onWebhookProgress)
    } else {
      await clearCache(activeStores, undefined, onWebhookProgress)
    }

    if (type === 'redeploy' && succeededStoreIds.length > 0) {
      patchBulkJob(queryClient, jobId, (job) => ({
        ...job,
        phase: 'deploy-status',
        deployPollTotal: succeededStoreIds.length,
        deployPollResolved: 0,
        deployPollStoreIds: succeededStoreIds,
      }))

      await pollBulkRedeployDeployStatus(queryClient, groupId, succeededStoreIds, jobId)
    }

    patchBulkJob(queryClient, jobId, (job) => ({
      ...job,
      phase: 'completed',
      status: 'completed',
    }))
    setBulkActionSession(queryClient, {
      groupId,
      jobId,
      type,
      status: 'completed',
      progressModalDismissed: getBulkActionSession(queryClient)?.progressModalDismissed ?? false,
      targetStoreIds: getBulkActionSession(queryClient)?.targetStoreIds,
    })

    return jobId
  } catch (error) {
    setBulkActionSession(queryClient, null)
    throw error
  }
}
